import { useMemo, useState } from 'react';
import { StackedBarChart, MiniBar, type Series } from '../components/charts';
import { DatePresets } from '../components/Filters';
import {
  COST_COLORS,
  COST_DATE_PRESETS,
  activeCostPreset,
  costPresetRange,
  downloadCsv,
  money,
  moneyCompact,
  num,
  pct,
  useApi,
  type CostDriverRow,
  type DeliveryCostResponse,
  type InvoiceRow,
  type InvoicesResponse,
} from '../lib/reports';

// The weekly chart plots cost-per-stop, a single efficiency series.
const COST_PER_STOP_SERIES: Series[] = [
  { key: 'cost_per_stop', label: 'Cost per stop', color: COST_COLORS.base_charge },
];

const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** Label a costing week by its Monday, e.g. "w/c 25 May". */
function formatWeek(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return `w/c ${d.getUTCDate()} ${MONTH[d.getUTCMonth()]}`;
}

interface CostFilters {
  from: string;
  to: string;
  status: string;
}

function defaultCostFilters(): CostFilters {
  // Cost is billed per costing week (Mon–Sun). Default to the last completed
  // costing week — it reliably has data (the current week isn't billed until it
  // closes). Widen with the Last month preset for the weekly trend comparison.
  const { from, to } = costPresetRange('last_week');
  return { from, to, status: '' };
}

type SortKey = 'total' | 'base_charge' | 'fuel_levy' | 'surcharge' | 'stops' | 'cost_per_stop';

export function DeliveryCost() {
  const [filters, setFilters] = useState<CostFilters>(defaultCostFilters);
  const patch = (p: Partial<CostFilters>) => setFilters((f) => ({ ...f, ...p }));

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (filters.from) p.set('from', filters.from);
    if (filters.to) p.set('to', filters.to);
    if (filters.status) p.set('status', filters.status);
    return p.toString();
  }, [filters]);

  const { data, error, loading } = useApi<DeliveryCostResponse>(`/api/reports/delivery-cost?${qs}`);
  const { data: invoices } = useApi<InvoicesResponse>(`/api/reports/delivery-cost/invoices?${qs}&limit=500`);

  const [sort, setSort] = useState<SortKey>('total');
  const s = data?.summary;
  const drivers = useMemo(
    () => [...(data?.by_driver ?? [])].sort((a, b) => Number(b[sort] ?? 0) - Number(a[sort] ?? 0)),
    [data, sort],
  );
  const maxDriverTotal = Math.max(1, ...drivers.map((d) => d.total));

  function exportInvoices() {
    const rows = (invoices?.rows ?? []).map((r: InvoiceRow) => [
      r.invoice_number,
      r.team_member_id,
      r.status,
      r.invoice_date,
      r.start_date,
      r.end_date,
      r.base_charge,
      r.fuel_levy,
      r.surcharge,
      r.sub_total,
      r.total_tax,
      r.total,
      r.stops,
      r.cost_per_stop,
    ]);
    downloadCsv(
      `amart-delivery-cost-${filters.from}_${filters.to}.csv`,
      [
        'invoice_number',
        'team_member_id',
        'status',
        'invoice_date',
        'period_start',
        'period_end',
        'base_charge_ex_gst',
        'fuel_levy_ex_gst',
        'surcharge_ex_gst',
        'sub_total_ex_gst',
        'gst',
        'total_inc_gst',
        'stops',
        'cost_per_stop',
      ],
      rows,
    );
  }

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <p className="eyebrow">Reporting / Delivery cost</p>
          <h1>Delivery cost</h1>
          <p className="muted">
            {s ? `${num(s.invoices)} invoices · ${num(s.drivers)} drivers · ` : ''}
            {filters.from} → {filters.to} · costs from team member (driver) invoices
          </p>
        </div>
      </header>

      {/* Dedicated filter bar — costing weeks (Mon–Sun), not the rolling
          presets used by the delivery reports. */}
      <div className="filters">
        <div className="filter">
          <span className="filter-label">Costing period</span>
          <div className="filter-range">
            <input
              type="date"
              value={filters.from}
              max={filters.to}
              onChange={(e) => patch({ from: e.target.value })}
            />
            <span className="filter-arrow">→</span>
            <input
              type="date"
              value={filters.to}
              min={filters.from}
              onChange={(e) => patch({ to: e.target.value })}
            />
          </div>
          <DatePresets
            from={filters.from}
            to={filters.to}
            onChange={patch}
            presets={COST_DATE_PRESETS}
            resolve={costPresetRange}
            active={activeCostPreset}
          />
          <span className="filter-hint">Costing weeks run Mon–Sun</span>
        </div>

        <div className="filter">
          <span className="filter-label">Status</span>
          <select value={filters.status} onChange={(e) => patch({ status: e.target.value })}>
            <option value="">All statuses</option>
            {(data?.statuses ?? []).map((st) => (
              <option key={st} value={st}>
                {st}
              </option>
            ))}
          </select>
        </div>

        <button type="button" className="btn-export" onClick={exportInvoices}>
          ↓ Export invoices
        </button>
      </div>

      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      {loading && !data && <p className="muted">Loading…</p>}

      {s && (
        <>
          <div className="hero">
            <div className="hero-main">
              <span className="hero-label">Total delivery cost</span>
              <span className="hero-value">{moneyCompact(s.total)}</span>
              <span className="hero-sub">incl. GST · {money(s.cost_per_stop)} per stop</span>
            </div>
            <div className="hero-stats">
              <div>
                <span className="hs-label">Base charge</span>
                <span className="hs-value">{money(s.base_charge)}</span>
                <span className="hs-foot">ex GST</span>
              </div>
              <div>
                <span className="hs-label">Fuel levy</span>
                <span className="hs-value">{money(s.fuel_levy)}</span>
                <span className="hs-foot">{pct(s.fuel_levy_pct)} of base</span>
              </div>
              <div>
                <span className="hs-label">Surcharge</span>
                <span className="hs-value">{money(s.surcharge)}</span>
                <span className="hs-foot">service lines</span>
              </div>
              <div>
                <span className="hs-label">GST</span>
                <span className="hs-value">{money(s.total_tax)}</span>
                <span className="hs-foot">{num(s.stops)} stops</span>
              </div>
            </div>
          </div>

          <div className="grid-2">
            <div className="card">
              <div className="card-head">
                <div>
                  <p className="eyebrow">Efficiency trend</p>
                  <h2>Cost per stop by costing week</h2>
                  <p className="muted small">
                    Total invoiced (inc GST) ÷ stops, per Mon–Sun week · lower is better
                  </p>
                </div>
              </div>
              <StackedBarChart
                data={data.weekly}
                series={COST_PER_STOP_SERIES}
                xKey="week_start"
                formatX={formatWeek}
                formatTotal={(v) => money(v)}
              />
            </div>

            <div className="card">
              <p className="eyebrow">Breakdown</p>
              <h2>Charge components</h2>
              <p className="muted small">Share of sub-total (ex GST)</p>
              <div className="tiles tiles-col">
                <ComponentBar
                  label="Base charge"
                  value={s.base_charge}
                  total={s.sub_total}
                  color={COST_COLORS.base_charge}
                />
                <ComponentBar
                  label="Fuel levy"
                  value={s.fuel_levy}
                  total={s.sub_total}
                  color={COST_COLORS.fuel_levy}
                />
                <ComponentBar
                  label="Surcharge"
                  value={s.surcharge}
                  total={s.sub_total}
                  color={COST_COLORS.surcharge}
                />
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <div>
                <h2>Cost by driver</h2>
                <p className="muted small">
                  {num(drivers.length)} team members · drivers shown by member ID (invoice contact name
                  isn&apos;t populated in the source data)
                </p>
              </div>
            </div>
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Driver</th>
                    <th>Invoices</th>
                    <SortTh label="Total (inc GST)" k="total" sort={sort} setSort={setSort} />
                    <SortTh label="Base charge" k="base_charge" sort={sort} setSort={setSort} />
                    <SortTh label="Fuel levy" k="fuel_levy" sort={sort} setSort={setSort} />
                    <SortTh label="Surcharge" k="surcharge" sort={sort} setSort={setSort} />
                    <SortTh label="Stops" k="stops" sort={sort} setSort={setSort} />
                    <SortTh label="Cost / stop" k="cost_per_stop" sort={sort} setSort={setSort} />
                  </tr>
                </thead>
                <tbody>
                  {drivers.map((d: CostDriverRow) => (
                    <tr key={d.team_member_id}>
                      <td className="cell-strong">Member #{d.team_member_id}</td>
                      <td>{num(d.invoices)}</td>
                      <td>
                        <div className="cell-bar">
                          <MiniBar value={d.total} max={maxDriverTotal} color={COST_COLORS.base_charge} />
                          <span>{money(d.total)}</span>
                        </div>
                      </td>
                      <td>{money(d.base_charge)}</td>
                      <td>{money(d.fuel_levy)}</td>
                      <td>{money(d.surcharge)}</td>
                      <td>{num(d.stops)}</td>
                      <td className="cell-strong">{money(d.cost_per_stop)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {drivers.length === 0 && <p className="muted">No invoices in this period.</p>}
          </div>

          <div className="card">
            <div className="card-head">
              <div>
                <h2>Invoices</h2>
                <p className="muted small">
                  Showing {num(invoices?.rows.length ?? 0)} of {num(invoices?.total ?? 0)} invoices
                </p>
              </div>
            </div>
            <InvoicesTable rows={invoices?.rows ?? []} />
          </div>

          <ol className="defs">
            <li>
              <b>Base charge</b> · The driver&apos;s per-delivery/run base rate, ex GST.
            </li>
            <li>
              <b>Fuel levy</b> · Fuel surcharge applied to the base charge, ex GST.
            </li>
            <li>
              <b>Surcharge</b> · Additional service line charges (e.g. waiting time, special handling), ex GST.
            </li>
            <li>
              <b>Cost per stop</b> · Total invoiced (inc GST) ÷ stops covered by the invoice.
            </li>
          </ol>
        </>
      )}
    </section>
  );
}

function ComponentBar({
  label,
  value,
  total,
  color,
}: {
  label: string;
  value: number;
  total: number;
  color: string;
}) {
  const share = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="tile static" style={{ borderTopColor: color }}>
      <span className="tile-label">{label}</span>
      <span className="tile-value">{money(value)}</span>
      <span className="tile-sub">{pct(share)} of sub-total</span>
      <MiniBar value={value} max={total} color={color} />
    </div>
  );
}

function SortTh({
  label,
  k,
  sort,
  setSort,
}: {
  label: string;
  k: SortKey;
  sort: SortKey;
  setSort: (k: SortKey) => void;
}) {
  return (
    <th className={`sortable${sort === k ? ' sorted' : ''}`} onClick={() => setSort(k)}>
      {label} {sort === k ? '▾' : ''}
    </th>
  );
}

function InvoicesTable({ rows }: { rows: InvoiceRow[] }) {
  if (rows.length === 0) return <p className="muted">No invoices match the current filters.</p>;
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Invoice</th>
            <th>Driver</th>
            <th>Status</th>
            <th>Period</th>
            <th>Base charge</th>
            <th>Fuel levy</th>
            <th>Surcharge</th>
            <th>GST</th>
            <th>Total</th>
            <th>Stops</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.team_member_invoice_id}>
              <td className="cell-strong">{r.invoice_number ?? `#${r.team_member_invoice_id}`}</td>
              <td>Member #{r.team_member_id}</td>
              <td>
                <span className={`cat cat-${(r.status ?? '').toLowerCase()}`}>{r.status ?? '—'}</span>
              </td>
              <td>
                {r.start_date ?? '—'}
                {r.end_date && r.end_date !== r.start_date ? ` – ${r.end_date}` : ''}
              </td>
              <td>{money(r.base_charge)}</td>
              <td>{money(r.fuel_levy)}</td>
              <td>{money(r.surcharge)}</td>
              <td>{money(r.total_tax)}</td>
              <td className="cell-strong">{money(r.total)}</td>
              <td>{num(r.stops ?? 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
